/**
 * DD-LINE-ID-CLEANUP-001 G2 — API path token; provisional keys never on the wire.
 *
 * Identity vs display:
 * - Identity: `lineKey` on the request subject (server / provisional / fixture source).
 * - Display: `sku` (catalog / product label) — never preferred as the wire token when
 *   `lineKeySource === 'server'` or `'fixture'`.
 * - Provisional live mint: wire uses legacy SKU when present; keyless rows refuse (no fabrication).
 */

import type { Request } from '@/lib/dd-queue/types'
import { findSubjectByLineKey } from '@/lib/dd-queue/lineKey'

export type ApiLineTokenStrategy = 'server_line_key' | 'legacy_sku_provisional'

export interface ApiLineTokenResult {
  /** Value placed on the API `lineKey` / path param. */
  token: string
  strategy: ApiLineTokenStrategy
  /** Display / legacy catalog sku for the same subject (null when keyless). */
  sku: string | null
  /** Client identity key (may differ from token when provisional). */
  lineKey: string
}

export interface ApiLineTokenReport {
  requestId: string
  lineKey: string
  sku: string
  strategy: ApiLineTokenStrategy
  action: string
}

export class ApiLineTokenRefusal extends Error {
  readonly report: ApiLineTokenReport

  constructor(report: ApiLineTokenReport, message: string) {
    super(message)
    this.name = 'ApiLineTokenRefusal'
    this.report = report
  }
}

/**
 * Choose the wire token for a mutation/upload.
 * Identity input is `lineKey`; display `sku` is returned for reporting only.
 * Provisional subjects: token = sku when present (legacy path). Keyless → refuse.
 */
export function resolveApiLineToken(req: Request, lineKey: string): ApiLineTokenResult {
  const subject = findSubjectByLineKey(req, lineKey)
  if (!subject?.lineKey) {
    throw new Error(`No line identity for ${req.id}:${lineKey}`)
  }
  const sku = subject.sku?.trim() || null
  if (subject.lineKeySource === 'provisional') {
    if (!sku) {
      throw new ApiLineTokenRefusal(
        {
          requestId: req.id,
          lineKey: subject.lineKey,
          sku: '',
          strategy: 'legacy_sku_provisional',
          action: 'refuse_no_legacy_token',
        },
        `Keyless line has no legacy wire token (${req.id}:${subject.lineKey})`
      )
    }
    return {
      token: sku,
      strategy: 'legacy_sku_provisional',
      sku,
      lineKey: subject.lineKey,
    }
  }
  return {
    token: subject.lineKey,
    strategy: 'server_line_key',
    sku,
    lineKey: subject.lineKey,
  }
}

/** Display helper: resolve catalog sku from identity lineKey (null when keyless). */
export function skuForLineKey(req: Request, lineKey: string): string | null {
  const sku = findSubjectByLineKey(req, lineKey)?.sku
  return sku?.trim() ? sku.trim() : null
}

/** True when identity is a client-minted provisional key (must not go on wire). */
export function isProvisionalLineKey(req: Request, lineKey: string): boolean {
  const subject = findSubjectByLineKey(req, lineKey)
  return subject?.lineKeySource === 'provisional'
}
